import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useState } from "react";
import { Campaign, CampaignStatus } from "schemas/dashboard";

const LOCKED_SETTINGS_HINT =
  "Once launched, a campaign's dates and schedule are locked. You can " +
  "rename, pause, or resume it — to change timing, duplicate it into a " +
  "new campaign.";

const COMPLETE_SETTINGS_HINT =
  "This campaign is complete. You can rename it, or duplicate it into a " +
  "new campaign to run it again.";

interface Props {
  campaign: Campaign;
  onRename: () => void;
  onEditSettings: () => void;
  onPause: () => void;
  onResume: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

export default function CampaignActionsMenu({
  campaign,
  onRename,
  onEditSettings,
  onPause,
  onResume,
  onDuplicate,
  onArchive,
  onDelete,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const run = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    action();
  };

  const canPause = campaign.status === CampaignStatus.in_progress;
  const canResume = campaign.status === CampaignStatus.paused;
  const canDelete = !campaign.hasSentEmails;
  const isComplete = campaign.status === CampaignStatus.complete;
  const isStarted =
    campaign.status === CampaignStatus.in_progress ||
    campaign.status === CampaignStatus.paused ||
    isComplete;
  const settingsHint = isComplete
    ? COMPLETE_SETTINGS_HINT
    : LOCKED_SETTINGS_HINT;

  return (
    <>
      <Tooltip title="More actions" placement="top">
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            color: grey[400],
            px: 1,
            borderRadius: 1.5,
            "&:hover": { color: grey[700] },
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-ellipsis-vertical"
            sx={{ fontSize: 15 }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableRestoreFocus
        sx={{ "& .MuiPaper-root": { minWidth: 220 } }}
      >
        <Tooltip title="Rename this campaign" placement="left">
          <MenuItem onClick={run(onRename)}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-pen" />
            </ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
        </Tooltip>

        {isStarted ? (
          <Tooltip title={settingsHint} placement="left">
            <Box component="span">
              <MenuItem disabled sx={{ whiteSpace: "normal", maxWidth: 280 }}>
                <ListItemIcon>
                  <Box component="i" className="fa-regular fa-gear" />
                </ListItemIcon>
                <ListItemText
                  primary="Edit Settings"
                  secondary={settingsHint}
                />
              </MenuItem>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Finish setting up this campaign" placement="left">
            <MenuItem onClick={run(onEditSettings)}>
              <ListItemIcon>
                <Box component="i" className="fa-regular fa-gear" />
              </ListItemIcon>
              <ListItemText>Edit Settings</ListItemText>
            </MenuItem>
          </Tooltip>
        )}

        <Divider />

        {canPause && (
          <Tooltip
            title="Pause sending and cancel scheduled pitches"
            placement="left"
          >
            <MenuItem onClick={run(onPause)}>
              <ListItemIcon>
                <Box component="i" className="fa-regular fa-pause" />
              </ListItemIcon>
              <ListItemText>Pause Campaign</ListItemText>
            </MenuItem>
          </Tooltip>
        )}
        {canResume && (
          <Tooltip title="Resume sending on a new schedule" placement="left">
            <MenuItem onClick={run(onResume)}>
              <ListItemIcon>
                <Box component="i" className="fa-regular fa-play" />
              </ListItemIcon>
              <ListItemText>Resume Campaign</ListItemText>
            </MenuItem>
          </Tooltip>
        )}
        <Tooltip
          title="Copy filters & templates into a new draft"
          placement="left"
        >
          <MenuItem onClick={run(onDuplicate)}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-copy" />
            </ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
        </Tooltip>

        <Divider />

        <Tooltip title="Move to the Archived tab" placement="left">
          <MenuItem onClick={run(onArchive)}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-box-archive" />
            </ListItemIcon>
            <ListItemText>Archive</ListItemText>
          </MenuItem>
        </Tooltip>

        {canDelete ? (
          <Tooltip title="Delete this campaign permanently" placement="left">
            <MenuItem onClick={run(onDelete)} sx={{ color: "error.main" }}>
              <ListItemIcon>
                <Box
                  component="i"
                  className="fa-regular fa-trash"
                  sx={{ color: "error.main" }}
                />
              </ListItemIcon>
              <ListItemText>Delete campaign</ListItemText>
            </MenuItem>
          </Tooltip>
        ) : (
          <Tooltip
            title="Campaigns that have sent emails can only be archived."
            placement="left"
          >
            <Box component="span">
              <MenuItem disabled>
                <ListItemIcon>
                  <Box component="i" className="fa-regular fa-trash" />
                </ListItemIcon>
                <ListItemText>Delete campaign</ListItemText>
              </MenuItem>
            </Box>
          </Tooltip>
        )}
      </Menu>
    </>
  );
}
